<template>
  <v-dialog v-model="dialog" max-width="650" class="pa-0 ma-0">
    <v-card>
      <!-- Title Bar -->
      <v-card-title class="redColor d-flex text-white">
        <span class="">{{ $t("home.search_part.label") }}</span>
        <v-spacer />
        <v-btn icon variant="outlined" color="white" @click="close">
          <v-icon> mdi-close </v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text>
        <v-row>
          <!-- Selector -->
          <v-col cols="12">
            <v-row align="center" justify="center" class="mb-2 pt-6">
              <v-btn
                :color="type === 'search' ? '#D32F2F' : ''"
                depressed
                class="mr-1"
                @click="switchTo('search')"
              >
                {{ $t("home.search_part.search") }}
              </v-btn>
              <v-btn
                :color="type === 'scan' ? '#D32F2F' : ''"
                depressed
                @click="switchTo('scan')"
              >
                {{ $t("home.search_part.scan") }}
              </v-btn>
            </v-row>
          </v-col>

          <!-- Search -->
          <v-col v-if="type === 'search'" cols="12" class="pa-3">
            <v-text-field
              v-model="search"
              variant="outlined"
              :label="$t('home.search_part.search')"
              color="red-darken-4"
              append-inner-icon="mdi-magnify"
              single-line
              hide-details
              :loading="loadingSearch"
              @keyup.enter="searchMethod"
              @click:append-inner="searchMethod()"
            />
          </v-col>

          <!-- Scan -->
          <v-col v-if="type === 'scan'" cols="12">
            <v-row>
              <!-- Camera Scanner -->
              <v-col v-if="!physicalScanner" cols="12">
                <qrcode-stream
                  v-if="dialog"
                  :key="cameraFacing"
                  :camera="cameraFacing"
                  :paused="paused"
                  @decode="decode"
                />
              </v-col>
              <!-- Physical Scanner Placeholder -->
              <v-col v-if="physicalScanner" cols="12">
                <!-- TODO: Implement physical scanner logic -->
                <v-alert type="info" border="bottom" variant="tonal">
                  Physical scanner functionality not yet implemented.
                </v-alert>
              </v-col>
              <!-- Loading Indicator -->
              <v-col v-if="loadingScan" cols="12">
                <v-row align="center" justify="center" class="ma-3">
                  <v-progress-circular
                    indeterminate
                    color="primary"
                    size="48"
                  />
                </v-row>
              </v-col>
            </v-row>
          </v-col>

          <!-- Message -->
          <v-col cols="12">
            <v-row align="center" justify="center" class="mt-2 mx-3">
              <v-alert
                :model-value="alert.value"
                :type="alert.type"
                variant="outlined"
                border="bottom"
              >
                {{ alert.message }}
              </v-alert>
            </v-row>
          </v-col>

          <!-- Location -->
          <v-col v-if="found" cols="12" class="mt-2">
            <v-row align="center" justify="center">
              <v-btn variant="outlined" @click="goToPart">
                {{ getStatus() }}
              </v-btn>
            </v-row>
          </v-col>
        </v-row>
      </v-card-text>

      <v-card-actions class="pr-4 pb-3">
        <v-btn v-if="type === 'scan'" icon flat @click="reScan">
          <v-icon>refresh</v-icon>
        </v-btn>
        <v-spacer />
        <v-btn v-if="type === 'scan'" icon flat @click="switchCamera">
          <v-icon>mdi-camera-switch</v-icon>
        </v-btn>
        <v-btn variant="outlined" color="error" @click="close">
          {{ $t("home.search_part.close") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import getRequest from "../../api/getRequest";
import { QrcodeStream } from "vue3-qrcode-reader";
export default {
  components: {
    QrcodeStream,
  },
  data: () => ({
    dialog: false,
    type: "search",
    loadingSearch: false,
    loadingScan: false,
    search: "",
    physicalScanner: false, // Not implemented at the moment.
    /** Prefer rear on mobile; 'auto' falls back to front/webcam on desktop. */
    cameraFacing: "auto",
    paused: false,
    decodedString: null,
    alert: {
      value: false,
      type: "success",
      message: "",
    },
    part: {
      location: null,
      id: null,
      status: null,
    },
    found: false,
  }),
  watch: {
    dialog(value) {
      if (!value) this.close();
    },
  },
  methods: {
    open() {
      this.cameraFacing = "auto";
      this.dialog = true;
      this.alert = {
        value: true,
        type: "info",
        message: this.$t("home.search_part.alert.insert_id"),
      };
    },
    close() {
      this.dialog = false;
      this.loadingSearch = false;
      this.loadingScan = false;
      this.search = "";
      this.paused = false;
      this.decodedString = null;
      this.alert.value = false;
      this.part = {
        location: null,
        id: null,
        status: null,
      };
      this.found = false;
      this.cameraFacing = "auto";
    },
    async searchMethod() {
      this.search = this.search.trim();
      this.loadingSearch = true;
      try {
        const response = await this.searchPart(this.search);
        this.found = true;
        this.part = {
          location: response.location,
          id: response.id,
          status: response.status,
        };
        this.displayAlert(true, response.id);
      } catch {
        this.found = false;
        this.displayAlert(false, this.search);
      }
      this.loadingSearch = false;
    },
    async decode(value) {
      if (value !== null && value.length > 0) {
        this.loadingScan = true;
        this.decodedString = value;
        this.paused = true;
        try {
          const response = await this.searchPart(this.decodedString);
          this.found = true;
          this.part = {
            location: response.location,
            id: response.id,
            status: response.status,
          };
          this.displayAlert(true, response.id);
        } catch {
          this.found = false;
          this.displayAlert(false, this.decodedString);
        }
        this.loadingScan = false;
      }
    },
    reScan() {
      this.found = false;
      this.decodedString = false;
      this.loadingScan = false;
      this.paused = false;
      this.cameraFacing = "auto";
      this.alert = {};
    },
    switchCamera() {
      this.cameraFacing = this.cameraFacing === "front" ? "rear" : "front";
    },
    searchPart(id) {
      return new Promise((resolve, reject) => {
        getRequest(
          `/part/search/${id}`,
          `role=${this.$store.getters.role}&institution_id=${this.$store.getters.institutionId}&user_id=${this.$store.getters.userId}`,
        )
          .then((response) => resolve(response))
          .catch((error) => reject(error));
      });
    },
    displayAlert(success, id) {
      if (success)
        this.alert = {
          value: true,
          type: "success",
          message: `${this.$t("home.search_part.alert.part_with_id")} ${id.slice(-6)} ${this.$t("home.search_part.alert.was_found")}`,
        };
      else
        this.alert = {
          value: true,
          type: "error",
          message: `${this.$t("home.search_part.alert.part_with_id")} ${id.slice(-6)} ${this.$t("home.search_part.alert.was_not_found")}`,
        };
    },
    getStatus() {
      if (this.part.location === "temporary-parts") {
        return this.$t("home.search_part.temporary_parts");
      }
      if (this.part.location === "parts") {
        if (
          this.part.status === 1 ||
          this.part.status === 2 ||
          this.part.status === 2.1 ||
          this.part.status === 3 ||
          this.part.status === 4
        )
          return this.$t("home.search_part.inventory");

        if (this.part.status === 5) return this.$t("home.search_part.sales");

        if (this.part.status === 6 || this.part.status === 6.1)
          return this.$t("home.search_part.lost");

        if (
          this.part.status === 7 ||
          this.part.status === 8 ||
          this.part.status === 8.1 ||
          this.part.status === 9
        )
          return this.$t("home.search_part.damaged");

        if (this.part.status === 10)
          return this.$t("home.search_part.reserved");

        if (this.part.status === 0) return this.$t("home.search_part.recycled");
      }

      return "";
    },
    goToPart() {
      this.part.id =
        this.part.id.length > 6 ? this.part.id.slice(-6) : this.part.id;
      if (this.part.location === "temporary-parts") {
        return this.$router.push(`/insert-parts?id=${this.part.id}`);
      }
      if (this.part.location === "parts") {
        if (
          this.part.status === 1 ||
          this.part.status === 2 ||
          this.part.status === 2.1 ||
          this.part.status === 3 ||
          this.part.status === 4
        )
          return this.$router.push(`/inventory?id=${this.part.id}`);

        if (this.part.status === 5)
          return this.$router.push(`/sales?id=${this.part.id}`);

        if (this.part.status === 6 || this.part.status === 6.1)
          return this.$router.push(`/lost?id=${this.part.id}`);

        if (
          this.part.status === 7 ||
          this.part.status === 8 ||
          this.part.status === 8.1 ||
          this.part.status === 9
        )
          return this.$router.push(`/damaged?id=${this.part.id}`);

        if (this.part.status === 10)
          return this.$router.push(`/ordered?id=${this.part.id}`);

        if (this.part.status === 0)
          return this.$router.push(`/recycled?id=${this.part.id}`);
      }

      return 0;
    },
    switchTo(type) {
      this.type = type;
      this.loadingSearch = false;
      this.loadingScan = false;
      this.search = "";
      this.paused = false;
      this.decodedString = null;
      this.alert = {
        value: true,
        type: "info",
        message: this.$t("home.search_part.alert.insert_id"),
      };
      this.part = {
        location: null,
        id: null,
        status: null,
      };
      this.found = false;
      this.cameraFacing = "auto";
    },
  },
};
</script>
